import React, { useState, useEffect } from "react";
import { Modal, Tabs, Space, Button, Typography, Upload, Input, Spin, Table, Card, Alert, message, Tag, Tooltip, Descriptions } from "antd";
import { InboxOutlined, FileExcelOutlined, FileTextOutlined, DownloadOutlined, CheckCircleOutlined, SyncOutlined, ScheduleOutlined, BarsOutlined, SettingOutlined } from "@ant-design/icons";
import { getImportSchema, requestParse, confirmImport } from "../../api/importApi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { COMMON_DATE_FIELDS } from "../../api/apiUtils";

dayjs.extend(utc);

const { TabPane } = Tabs;
const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

/**
 * 通用导入对话框组件
 * @param {Object} props - 组件属性
 * @param {boolean} props.isOpen - 是否显示对话框
 * @param {function} props.onClose - 关闭对话框回调
 * @param {string} props.importType - 导入类型标识符
 * @param {Object} [props.extraData] - 随确认请求一起上传的额外参数
 * @param {function} [props.onSuccess] - 标准导入成功回调 (数据已入库)
 * @param {function} [props.onError] - 导入失败回调
 * @param {function} [props.onDataParsed] - (可选) 解析成功回调，用于将数据导入前端状态而非数据库。如果提供此回调，则解析成功后直接调用并关闭，跳过预览和确认导入步骤。
 */
const ImportModal = ({ isOpen, onClose, importType, extraData, onSuccess, onError, onDataParsed }) => {
    // 组件状态
    const [schema, setSchema] = useState(null);
    const [loading, setLoading] = useState(false);
    const [schemaLoading, setSchemaLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("file");
    const [fileList, setFileList] = useState([]);
    const [textContent, setTextContent] = useState("");
    const [parseInstruction, setParseInstruction] = useState("");
    const [previewData, setPreviewData] = useState(null);
    const [previewId, setPreviewId] = useState(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // 获取导入Schema
    useEffect(() => {
        if (isOpen && importType) {
            fetchSchema();
        }
    }, [isOpen, importType]);

    // 重置状态
    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen]);

    // 获取Schema函数
    const fetchSchema = async () => {
        try {
            setSchemaLoading(true);
            setErrorMessage(null);
            const schemaData = await getImportSchema(importType);
            setSchema(schemaData);
            // 根据Schema设置AI提示（如果存在且用户未手动输入）
            if (schemaData?.aiPromptHint && !parseInstruction) {
                setParseInstruction(schemaData.aiPromptHint);
            }
        } catch (error) {
            console.error("获取Schema失败:", error);
            setErrorMessage("无法获取导入配置，请刷新页面或联系管理员");
            if (onError) {
                onError(error);
            }
        } finally {
            setSchemaLoading(false);
        }
    };

    // 重置状态函数
    const resetState = () => {
        setFileList([]);
        setTextContent("");
        setParseInstruction("");
        setPreviewData(null);
        setPreviewId(null);
        setErrorMessage(null);
        setActiveTab("file");
        setSchema(null); // 关闭时重置schema
        setSchemaLoading(false);
        setLoading(false);
        setConfirmLoading(false);
    };

    // 处理文件变更
    const handleFileChange = ({ fileList }) => {
        setFileList(fileList.slice(-1)); // 只保留最后一个文件
        setPreviewData(null);
        setPreviewId(null);
        setErrorMessage(null); // 清除错误信息
    };

    // 处理解析指导变更
    const handleParseInstructionChange = (e) => {
        setParseInstruction(e.target.value);
    };

    /**
     * 处理Excel导入的日期时间字段
     * @param {Array|Object} data - 解析后的预览数据
     * @returns {Array|Object} - 处理后的数据
     */
    const processImportDateFields = (data) => {
        if (!data) return data;

        // 如果是数组，处理每个元素
        if (Array.isArray(data)) {
            return data.map((item) => processImportDateFields(item));
        }

        // 如果是对象，且不是普通对象，直接返回
        if (typeof data !== "object" || data === null) {
            return data;
        }

        // 创建结果对象的副本
        const result = { ...data };

        // 标准时间格式
        const TIME_FORMAT = "HH:mm:ss";
        const DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";

        // 特殊处理 day_of_week 字段（星期几）
        if (result.day_of_week !== undefined && !isNaN(result.day_of_week)) {
            // 确保 day_of_week 是数字格式
            const dayNum = Number(result.day_of_week);
            // 如果值是1-7，确保它符合系统的期望格式
            if (dayNum >= 1 && dayNum <= 7) {
                // 保持数字格式，但确保是整数
                result.day_of_week = Math.floor(dayNum);
            }
        }

        // 检查每个字段
        Object.keys(result).forEach((key) => {
            // 递归处理嵌套对象或数组
            if (typeof result[key] === "object" && result[key] !== null) {
                result[key] = processImportDateFields(result[key]);
                return;
            }

            // 只处理常见的日期时间字段
            if (!COMMON_DATE_FIELDS.includes(key)) {
                return;
            }

            const value = result[key];
            // 跳过空值
            if (value === null || value === undefined || value === "") {
                return;
            }

            // 1. 检查是否只有时间部分 (如 "06:15:00")
            if (typeof value === "string" && /^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
                // 如果记录中有date字段，使用它作为日期部分
                if (result.date && typeof result.date === "string") {
                    try {
                        // 尝试结合日期和时间
                        const dateTime = dayjs.utc(`${result.date}T${value}`);
                        if (dateTime.isValid()) {
                            result[key] = dateTime.format(DATE_TIME_FORMAT) + "Z";
                        }
                    } catch (error) {
                        console.error(`处理${key}字段时发生错误:`, error);
                    }
                }
                // 如果没有date字段，可以尝试使用flight_date或schedule_date等字段
                else if (result.flight_date && typeof result.flight_date === "string") {
                    try {
                        const dateTime = dayjs.utc(`${result.flight_date}T${value}`);
                        if (dateTime.isValid()) {
                            result[key] = dateTime.format(DATE_TIME_FORMAT) + "Z";
                        }
                    } catch (error) {
                        console.error(`处理${key}字段时发生错误:`, error);
                    }
                } else if (result.schedule_date && typeof result.schedule_date === "string") {
                    try {
                        const dateTime = dayjs.utc(`${result.schedule_date}T${value}`);
                        if (dateTime.isValid()) {
                            result[key] = dateTime.format(DATE_TIME_FORMAT) + "Z";
                        }
                    } catch (error) {
                        console.error(`处理${key}字段时发生错误:`, error);
                    }
                }
            }
            // 2. 检查是否已经是完整日期时间，需要标准化格式
            else if (typeof value === "string" && /\d{4}-\d{2}-\d{2}/.test(value)) {
                try {
                    const dateTime = dayjs.utc(value);
                    if (dateTime.isValid()) {
                        // 如果已经包含时间部分，使用完整格式
                        if (value.includes("T") || value.includes(" ")) {
                            result[key] = dateTime.format(DATE_TIME_FORMAT) + "Z";
                        }
                        // 否则仅标准化日期部分
                    }
                } catch (error) {
                    console.error(`处理${key}字段时发生错误:`, error);
                }
            }
        });

        return result;
    };

    // 发起解析请求
    const handleParse = async () => {
        // 重置之前的预览和错误
        setPreviewData(null);
        setPreviewId(null);
        setErrorMessage(null);

        try {
            setLoading(true);

            // 验证输入
            if (activeTab === "file" && fileList.length === 0) {
                message.error("请选择要导入的文件");
                setLoading(false); // 结束加载状态
                return;
            }
            if (activeTab === "text" && !textContent.trim()) {
                message.error("请输入要解析的文本内容");
                setLoading(false); // 结束加载状态
                return;
            }

            // 准备请求参数
            const dataContent = activeTab === "file" ? fileList[0].originFileObj : textContent;
            const inputType = activeTab === "file" ? "file" : "text";

            // 请求参数对象
            const requestParams = {
                importType,
                dataContent,
                inputType,
            };

            // 只有当parseInstruction有值时才添加到请求参数中
            if (parseInstruction && parseInstruction.trim()) {
                requestParams.parseInstruction = parseInstruction.trim();
            }

            // 发送解析请求
            const result = await requestParse(requestParams);

            // 处理预览数据中的日期时间字段
            if (result && result.preview_data) {
                // 处理预览数据中的日期时间字段
                result.preview_data = processImportDateFields(result.preview_data);
            }

            // 调试：打印完整的返回结果
            console.log("API返回结果:", result);
            console.log("是否包含warnings:", "warnings" in result);
            console.log("warnings内容:", result.warnings);

            // 检查是否有警告信息 - 在 onDataParsed 分支之前处理
            let hasWarnings = false;
            if (result.warnings) {
                hasWarnings = true;
                const warnings = result.warnings;

                // 构建详细的警告信息
                let warningDetails = [];

                if (warnings.missing_required_fields && warnings.missing_required_fields.length > 0) {
                    // 使用带建议的详细信息，如果有的话
                    const missingFieldsInfo = warnings.missing_fields_with_suggestions ||
                        warnings.missing_required_fields.map(field => ({ field, suggested_names: [] }));

                    warningDetails.push({
                        type: "缺少必要列",
                        items: missingFieldsInfo,
                        description: "这些列是必需的，缺少会影响数据完整性"
                    });
                }

                if (warnings.unmapped_columns && warnings.unmapped_columns.length > 0) {
                    warningDetails.push({
                        type: "未识别的列",
                        items: warnings.unmapped_columns,
                        description: "这些列无法识别，数据将被忽略"
                    });
                }

                // 使用 Modal 显示详细的警告信息
                Modal.warning({
                    title: "Excel 导入警告",
                    width: 600,
                    content: (
                        <div style={{ marginTop: 16 }}>
                            <Alert
                                message="数据解析成功，但发现以下问题："
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            {warningDetails.map((detail, index) => (
                                <div key={index} style={{ marginBottom: 16 }}>
                                    <h4 style={{ color: '#fa8c16', marginBottom: 8 }}>
                                        {detail.type} ({detail.items.length} 个)
                                    </h4>
                                    <p style={{ color: '#666', fontSize: '12px', marginBottom: 8 }}>
                                        {detail.description}
                                    </p>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {detail.items.map((item, itemIndex) => {
                                            // 处理缺少必要列的情况，显示建议的列名
                                            if (detail.type === "缺少必要列" && typeof item === 'object' && item.field) {
                                                return (
                                                    <li key={itemIndex} style={{ color: '#d46b08', marginBottom: 8 }}>
                                                        <div>
                                                            <code style={{ fontWeight: 'bold' }}>{item.field}</code>
                                                            {item.suggested_names && item.suggested_names.length > 0 && (
                                                                <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                                                                    建议使用列名: {item.suggested_names.map((name, nameIndex) => (
                                                                        <span key={nameIndex}>
                                                                            <code style={{
                                                                                backgroundColor: '#f0f0f0',
                                                                                padding: '2px 4px',
                                                                                borderRadius: '2px',
                                                                                color: '#1890ff'
                                                                            }}>
                                                                                {name}
                                                                            </code>
                                                                            {nameIndex < item.suggested_names.length - 1 ? ', ' : ''}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </li>
                                                );
                                            } else {
                                                // 处理其他类型的警告（如未识别的列）
                                                const displayItem = typeof item === 'object' ? item.field || item : item;
                                                return (
                                                    <li key={itemIndex} style={{ color: '#d46b08' }}>
                                                        <code>{displayItem}</code>
                                                    </li>
                                                );
                                            }
                                        })}
                                    </ul>
                                </div>
                            ))}
                            <Alert
                                message="建议解决方案"
                                description={
                                    <div>
                                        <p>1. 检查 Excel 表头是否与系统要求的列名一致</p>
                                        <p>2. 确保必要的列都存在且命名正确</p>
                                        <p>3. 如有疑问，请联系管理员确认列名映射规则</p>
                                    </div>
                                }
                                type="info"
                                showIcon
                                style={{ marginTop: 16 }}
                            />
                        </div>
                    ),
                    okText: "我知道了",
                });

                // 同时显示简短的消息提示
                message.warning("Excel 导入存在问题，请查看详细信息");
            }

            // 如果有 onDataParsed 回调，直接调用并关闭
            if (onDataParsed) {
                if (!hasWarnings) {
                    message.success("数据解析成功");
                }
                onDataParsed(result.preview_data); // 将处理后的数据传递给回调
                onClose(); // 直接关闭模态框
                return; // 结束函数执行，不进行后续的预览和确认设置
            }

            // 设置预览数据和ID (标准流程)
            setPreviewData(result.preview_data);
            setPreviewId(result.preview_id);

            if (!hasWarnings) {
                message.success("解析成功，请预览下方结果");
            }
        } catch (error) {
            console.error("解析失败:", error);
            const detail = error.response?.data?.detail;
            let errorMsg = "解析过程中出现错误";

            // 处理验证错误（422状态码）
            if (error.response?.status === 422 && detail && typeof detail === "object") {
                if (detail.validation_errors && Array.isArray(detail.validation_errors)) {
                    // 构建详细的错误信息
                    const validationErrors = detail.validation_errors;
                    const errorSummary = detail.message || "Excel 识别有问题";

                    // 统计列名不匹配的错误
                    const missingColumnErrors = validationErrors.filter(err => err.error_type === "missing_column");
                    const otherErrors = validationErrors.filter(err => err.error_type !== "missing_column");

                    let detailedMsg = errorSummary + "\n\n";

                    if (missingColumnErrors.length > 0) {
                        detailedMsg += "列名不匹配的问题：\n";
                        missingColumnErrors.forEach(err => {
                            detailedMsg += `• 缺少列：${err.column}\n`;
                        });
                        detailedMsg += "\n建议：请检查 Excel 表头是否与模板一致\n\n";
                    }

                    if (otherErrors.length > 0) {
                        detailedMsg += "其他数据问题：\n";
                        otherErrors.slice(0, 5).forEach(err => {
                            detailedMsg += `• 第${err.row}行，列"${err.column}"：${err.message}\n`;
                        });
                        if (otherErrors.length > 5) {
                            detailedMsg += `• 还有 ${otherErrors.length - 5} 个类似问题...\n`;
                        }
                    }

                    errorMsg = detailedMsg;
                } else {
                    errorMsg = detail.message || JSON.stringify(detail);
                }
            } else if (typeof detail === "string") {
                errorMsg = detail;
            } else if (detail && typeof detail === "object") {
                // 尝试提取更具体的错误信息
                errorMsg = detail.message || JSON.stringify(detail);
            }

            setErrorMessage(errorMsg);
            message.error(detail?.message || errorMsg); // 同时用 message 提示
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    // 确认导入
    const handleConfirm = async () => {
        // --- 重要: 如果 onDataParsed 存在，理论上不应能调用到这里，但作为防御性编程 ---
        if (onDataParsed) {
            console.warn("ImportModal: handleConfirm should not be called when onDataParsed is provided.");
            onClose();
            return;
        }
        // --- 结束防御性编程 ---

        try {
            setConfirmLoading(true);
            setErrorMessage(null);

            // 验证预览ID
            if (!previewId) {
                message.error("请先成功解析数据");
                return;
            }

            // 发送确认请求
            const result = await confirmImport(previewId, extraData);

            // 默认的成功消息
            let successMsg = "导入成功";
            // 尝试从后端结果获取更详细的摘要信息
            if (result && result.summary) {
                successMsg = result.summary;
            } else if (result && result.message) {
                successMsg = result.message;
            }

            message.success(successMsg);

            // 调用成功回调
            if (onSuccess) {
                onSuccess(result);
            }

            // 关闭对话框
            onClose();
        } catch (error) {
            console.error("确认导入失败:", error);
            const detail = error.response?.data?.detail;
            let errorMsg = "导入过程中出现错误，请重试";

            // 处理验证错误（422状态码）
            if (error.response?.status === 422 && detail && typeof detail === "object") {
                if (detail.validation_errors && Array.isArray(detail.validation_errors)) {
                    // 构建详细的错误信息
                    const validationErrors = detail.validation_errors;
                    const errorSummary = detail.message || "数据验证失败";

                    // 统计列名不匹配的错误
                    const missingColumnErrors = validationErrors.filter(err => err.error_type === "missing_column");
                    const otherErrors = validationErrors.filter(err => err.error_type !== "missing_column");

                    let detailedMsg = errorSummary + "\n\n";

                    if (missingColumnErrors.length > 0) {
                        detailedMsg += "列名不匹配的问题：\n";
                        missingColumnErrors.forEach(err => {
                            detailedMsg += `• 缺少列：${err.column}\n`;
                        });
                        detailedMsg += "\n建议：请检查 Excel 表头是否与模板一致\n\n";
                    }

                    if (otherErrors.length > 0) {
                        detailedMsg += "其他数据问题：\n";
                        otherErrors.slice(0, 5).forEach(err => {
                            detailedMsg += `• 第${err.row}行，列"${err.column}"：${err.message}\n`;
                        });
                        if (otherErrors.length > 5) {
                            detailedMsg += `• 还有 ${otherErrors.length - 5} 个类似问题...\n`;
                        }
                    }

                    errorMsg = detailedMsg;
                } else {
                    errorMsg = detail.message || JSON.stringify(detail);
                }
            } else if (typeof detail === "string") {
                errorMsg = detail;
            } else if (detail && typeof detail === "object") {
                // 尝试提取更具体的错误信息
                errorMsg = detail.message || JSON.stringify(detail);
            }

            setErrorMessage(errorMsg);
            message.error(detail?.message || errorMsg); // 同时用 message 提示
            if (onError) {
                onError(error);
            }
        } finally {
            setConfirmLoading(false);
        }
    };

    // ------------------- 预览渲染逻辑 -------------------

    // 获取通用表格列定义 (基于 schema.fields 或 schema.sheets[x].fields)
    const getGenericColumns = (fields) => {
        if (!fields || !fields.length) {
            return [];
        }
        return fields.map((field) => ({
            title: field.label || field.name,
            dataIndex: field.name,
            key: field.name,
            render: (text, record) => {
                let displayValue = text;
                // 简单处理数组和对象显示
                if (Array.isArray(text)) {
                    displayValue = text.join(", ");
                } else if (typeof text === "object" && text !== null) {
                    displayValue = JSON.stringify(text);
                }
                // 对必填项添加标记
                const isFieldRequired = typeof field.required === "function" ? field.required(record) : field.required;
                if (isFieldRequired && (displayValue === null || displayValue === undefined || displayValue === "")) {
                    return <Text type="warning">{String(displayValue ?? "(空)")}</Text>;
                }
                // 确保渲染的是字符串，避免React警告
                return String(displayValue ?? "");
            },
        }));
    };

    // 渲染 ScheduleMasterDetail 预览 (多 Tab 页)
    const renderScheduleMasterDetailPreview = (data) => {
        if (!data || !Array.isArray(data) || !schema?.sheets) return null;

        // 先处理数据中的日期时间字段
        const processedData = data.map((group) => {
            // 创建一个新的组对象
            const processedGroup = { ...group };

            // 获取各部分的 key 名
            const scheduleConfig = schema.sheets.find((s) => s.model === schema.primarySheet || s.sheetName === schema.primarySheet || s.model === "Schedule" || s.sheetName === "Schedule");
            const flightConfig = schema.sheets.find((s) => s.model === "ScheduleFlight" || s.sheetName === "ScheduleFlight");
            const parameterConfig = schema.sheets.find((s) => s.model === "ScheduleParameter" || s.sheetName === "ScheduleParameter");

            const scheduleModelKey = scheduleConfig?.model || scheduleConfig?.sheetName || "Schedule";
            const flightModelKey = flightConfig?.model || flightConfig?.sheetName || "ScheduleFlight";
            const parameterModelKey = parameterConfig?.model || parameterConfig?.sheetName || "ScheduleParameter";

            // 处理每个部分的数据
            if (processedGroup[scheduleModelKey]) {
                processedGroup[scheduleModelKey] = processImportDateFields(processedGroup[scheduleModelKey]);
            }

            if (processedGroup[flightModelKey]) {
                processedGroup[flightModelKey] = processImportDateFields(processedGroup[flightModelKey]);
            }

            if (processedGroup[parameterModelKey]) {
                processedGroup[parameterModelKey] = processImportDateFields(processedGroup[parameterModelKey]);
            }

            return processedGroup;
        });

        const linkKey = schema.linkKey || "schedule_name"; // 获取关联键

        // 根据 schema 获取各 sheet 的配置
        const getSheetConfig = (modelOrSheetName) => schema.sheets.find((s) => s.model === modelOrSheetName || s.sheetName === modelOrSheetName);

        const scheduleConfig = getSheetConfig(schema.primarySheet || "Schedule");
        const flightConfig = getSheetConfig("ScheduleFlight");
        const parameterConfig = getSheetConfig("ScheduleParameter");

        // 提取各部分的 key 名 (重要: 确保与后端返回的结构化数据键名匹配)
        // 假设后端 IntelligentParsingService 返回的字典 key 直接用了 schema 中的 model 或 sheetName
        const scheduleModelKey = scheduleConfig?.model || scheduleConfig?.sheetName || "Schedule";
        const flightModelKey = flightConfig?.model || flightConfig?.sheetName || "ScheduleFlight";
        const parameterModelKey = parameterConfig?.model || parameterConfig?.sheetName || "ScheduleParameter";

        return (
            <div style={{ marginTop: 16 }}>
                <Title level={5} style={{ marginBottom: 16 }}>
                    <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                    解析结果预览 ({processedData.length} 个计划组)
                </Title>
                {processedData.map((group, index) => {
                    // 从 group 中安全地获取数据
                    // 主表数据通常是列表，即使只有一个计划信息，也取第一个元素
                    const scheduleDataList = group[scheduleModelKey] || [];
                    const scheduleData = scheduleDataList[0] || {}; // 取列表的第一个元素

                    const flightsData = group[flightModelKey] || []; // 从表数据 (列表)
                    const parameterDataArray = group[parameterModelKey] || []; // 参数数据 (列表，但通常只有一个)
                    const parameterData = parameterDataArray[0] || {}; // 取第一个参数，给默认值

                    const groupIdentifier = scheduleData?.[linkKey] || `组 ${index + 1}`;

                    return (
                        <Card
                            key={index}
                            title={`航班计划组: ${groupIdentifier}`}
                            size="small"
                            style={{ marginBottom: 16 }}
                            extra={
                                <Tag color="blue">
                                    含 {flightsData.length} 条航班,
                                    {Object.keys(parameterData).length > 0 ? " 有参数" : " 无参数"}
                                </Tag>
                            }
                        >
                            <Tabs defaultActiveKey="schedule" size="small">
                                <TabPane
                                    tab={
                                        <span>
                                            <ScheduleOutlined /> 计划信息
                                        </span>
                                    }
                                    key="schedule"
                                >
                                    {scheduleConfig && Object.keys(scheduleData).length > 0 ? (
                                        <Descriptions bordered size="small" column={1}>
                                            {scheduleConfig.fields.map((field) => (
                                                <Descriptions.Item key={field.name} label={field.label || field.name}>
                                                    {scheduleData[field.name] !== undefined && scheduleData[field.name] !== null ? String(scheduleData[field.name]) : <i>(空)</i>}
                                                </Descriptions.Item>
                                            ))}
                                        </Descriptions>
                                    ) : (
                                        <Text type="secondary">无计划信息或配置错误</Text>
                                    )}
                                </TabPane>
                                <TabPane
                                    tab={
                                        <span>
                                            <BarsOutlined /> 航班列表 ({flightsData.length})
                                        </span>
                                    }
                                    key="flights"
                                >
                                    {flightConfig && flightsData.length > 0 ? (
                                        <Table
                                            dataSource={flightsData}
                                            columns={getGenericColumns(flightConfig.fields)}
                                            size="small"
                                            rowKey={(record, i) => `${index}-${record.flight_number || i}`} // 尝试用更有意义的key
                                            pagination={{ pageSize: 5, showSizeChanger: false, size: "small" }}
                                            scroll={{ x: "max-content" }}
                                        />
                                    ) : (
                                        <Text type="secondary">无航班信息</Text>
                                    )}
                                </TabPane>
                                <TabPane
                                    tab={
                                        <span>
                                            <SettingOutlined /> 参数配置
                                        </span>
                                    }
                                    key="parameter"
                                >
                                    {parameterConfig && Object.keys(parameterData).length > 0 ? (
                                        <Descriptions bordered size="small" column={1}>
                                            {parameterConfig.fields.map((field) => (
                                                <Descriptions.Item key={field.name} label={field.label || field.name}>
                                                    {/* 特殊处理 JSON 字段 */}
                                                    {field.name === "parameter_json" && typeof parameterData[field.name] === "object" ? (
                                                        <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{JSON.stringify(parameterData[field.name], null, 2)}</pre>
                                                    ) : parameterData[field.name] !== undefined && parameterData[field.name] !== null ? (
                                                        String(parameterData[field.name])
                                                    ) : (
                                                        <i>(空)</i>
                                                    )}
                                                </Descriptions.Item>
                                            ))}
                                        </Descriptions>
                                    ) : (
                                        <Text type="secondary">无参数配置信息</Text>
                                    )}
                                </TabPane>
                            </Tabs>
                        </Card>
                    );
                })}
                {/* 确认按钮移到所有组预览下方 */}
                <div style={{ marginTop: 16, textAlign: "right" }}>
                    <Button type="primary" onClick={handleConfirm} loading={confirmLoading} disabled={loading || !previewId}>
                        确认导入 {processedData.length} 个计划组
                    </Button>
                </div>
            </div>
        );
    };

    // 渲染通用预览 (单表)
    const renderGenericPreview = () => {
        // 确保 previewData 是数组且 schema 有 fields
        if (!previewData || !Array.isArray(previewData) || previewData.length === 0 || !schema?.fields) return null;

        // 处理日期时间字段
        const processedData = processImportDateFields(previewData);

        const columns = getGenericColumns(schema.fields);

        return (
            <div style={{ marginTop: 16 }}>
                <Card
                    title={
                        <Space>
                            <CheckCircleOutlined style={{ color: "#52c41a" }} />
                            <span>解析结果</span>
                            <Tag color="success">成功解析 {processedData.length} 条记录</Tag>
                        </Space>
                    }
                    size="small"
                >
                    <Table dataSource={processedData} columns={columns} size="small" rowKey={(record, index) => index} pagination={{ pageSize: 5, showSizeChanger: false }} scroll={{ x: "max-content" }} />
                    <div style={{ marginTop: 16, textAlign: "right" }}>
                        <Button type="primary" onClick={handleConfirm} loading={confirmLoading} disabled={loading || !previewId}>
                            确认导入
                        </Button>
                    </div>
                </Card>
            </div>
        );
    };

    // 渲染预览区域 (根据 importType 选择渲染器)
    const renderPreview = () => {
        // 如果提供了 onDataParsed 回调，则不渲染预览区域，因为解析成功后会直接关闭
        if (onDataParsed) {
            return null;
        }

        if (!previewData) {
            return null;
        }

        // 处理 schedule_master_detail 的特殊情况
        if (importType === "schedule_master_detail" && schema?.sheets) {
            // 针对后端返回的是对象（而非数组）的情况进行处理
            if (!Array.isArray(previewData) && typeof previewData === "object") {
                const transformedData = [previewData];
                return renderScheduleMasterDetailPreview(transformedData);
            } else if (Array.isArray(previewData)) {
                // 数组格式，直接传递
                return renderScheduleMasterDetailPreview(previewData);
            } else {
                // 既不是对象也不是数组，无法处理
                console.warn("无法处理的预览数据格式:", previewData);
                return <Alert message="预览数据格式错误或配置缺失" type="warning" showIcon style={{ marginTop: 16 }} />;
            }
        } else if (schema?.fields) {
            // 通用单表格式
            if (Array.isArray(previewData)) {
                return renderGenericPreview();
            } else {
                console.warn("单表格式预期数组但收到:", previewData);
                return <Alert message="预览数据格式错误或配置缺失" type="warning" showIcon style={{ marginTop: 16 }} />;
            }
        } else {
            // Schema结构不完整
            return <Alert message="无法根据当前配置渲染预览" type="warning" showIcon style={{ marginTop: 16 }} />;
        }
    };

    // ------------------- 主对话框渲染 -------------------

    // 渲染Schema加载状态
    if (schemaLoading && isOpen) {
        // 只有在打开时才显示加载
        return (
            <Modal title="准备导入" open={isOpen} onCancel={onClose} footer={null} width={800}>
                <div style={{ textAlign: "center", padding: "50px 0" }}>
                    <Spin size="large" />
                </div>
            </Modal>
        );
    }

    // 渲染主对话框
    return (
        <Modal title={schema?.description || "数据导入"} open={isOpen} onCancel={onClose} footer={null} width={importType === "schedule_master_detail" ? 1000 : 800} maskClosable={false} destroyOnHidden>
            <Spin spinning={loading || confirmLoading} tip={loading ? "解析中..." : confirmLoading ? "导入中..." : ""}>
                {loading || confirmLoading ? (
                    // loading 时用空 div 占位，保证 Spin 有 children
                    <div style={{ minHeight: 200 }} />
                ) : (
                    <div>
                        {errorMessage && <Alert message="操作失败" description={errorMessage} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setErrorMessage(null)} />}

                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => {
                                setActiveTab(key);
                                setPreviewData(null);
                                setPreviewId(null);
                                setErrorMessage(null);
                                setFileList([]);
                                setTextContent("");
                            }}
                        >
                            <TabPane
                                tab={
                                    <span>
                                        <FileExcelOutlined /> Excel/CSV 文件导入
                                    </span>
                                }
                                key="file"
                                disabled={loading || confirmLoading}
                            >
                                <Dragger name="file" multiple={false} fileList={fileList} onChange={handleFileChange} beforeUpload={() => false} accept=".xlsx, .xls, .csv" disabled={loading || confirmLoading}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="ant-upload-text">点击或拖拽 Excel/CSV 文件到此区域进行上传</p>
                                    <p className="ant-upload-hint">
                                        支持单文件导入。请确保文件格式符合要求。
                                        {schema?.excelTemplateUrl && (
                                            <>
                                                <br />
                                                <a href={schema.excelTemplateUrl} download>
                                                    <DownloadOutlined /> 下载模板文件
                                                </a>
                                            </>
                                        )}
                                    </p>
                                </Dragger>
                            </TabPane>
                            <TabPane
                                tab={
                                    <span>
                                        <FileTextOutlined /> 文本内容导入
                                    </span>
                                }
                                key="text"
                            >
                                <Alert message="该功能正在开发中，敬请期待！" type="info" showIcon style={{ margin: "24px 0" }} />
                            </TabPane>
                        </Tabs>

                        {/* 解析指导 (AI 参考) 区域：只在文本内容导入Tab显示，且禁用输入 */}
                        {activeTab === "text" && (
                            <div style={{ marginTop: 16 }}>
                                <Text strong>解析指导 (AI 参考):</Text>
                                <TextArea rows={2} placeholder="如果需要，可以在此提供额外的解析说明或要求，辅助 AI 理解。留空则使用默认 Schema 提示。" value={parseInstruction} onChange={handleParseInstructionChange} disabled={true} style={{ marginTop: 8 }} />
                            </div>
                        )}

                        <div style={{ marginTop: 16, textAlign: "right" }}>
                            <Button type="primary" icon={<SyncOutlined />} onClick={handleParse} loading={loading} disabled={confirmLoading || (activeTab === "file" && fileList.length === 0) || (activeTab === "text" && !textContent.trim())}>
                                解析并预览
                            </Button>
                        </div>

                        {renderPreview()}

                        {errorMessage && !previewData && <Alert message="解析失败" description={errorMessage} type="error" showIcon style={{ marginTop: 16 }} />}
                    </div>
                )}
            </Spin>
            {/* 确认按钮，仅在标准流程（无onDataParsed）且有预览数据时显示 */}
            {!onDataParsed && previewData && (
                <Space style={{ marginTop: 24, justifyContent: "flex-end", width: "100%" }}>
                    <Button onClick={onClose}>取消</Button>
                    <Button
                        type="primary"
                        onClick={handleConfirm}
                        loading={confirmLoading}
                        disabled={!previewId} // 确保有预览ID才能确认
                    >
                        确认导入
                    </Button>
                </Space>
            )}
        </Modal>
    );
};

export default ImportModal;
